(() => {
  if (customElements.get('g2g-cart-shipping')) return;

  class G2GCartShipping extends HTMLElement {
    connectedCallback() {
      this.revision = (this.revision || 0) + 1;
      this.onCartUpdated = this.onCartUpdated || ((event) => {
        const cart = event.detail && event.detail.cart;
        if (!cart || !Array.isArray(cart.items)) return;
        const total = cart.items
          .filter((item) => item.requires_shipping)
          .reduce((sum, item) => sum + item.final_line_price, 0);
        const discount = (cart.cart_level_discount_applications || [])
          .reduce((sum, item) => sum + item.total_allocated_amount, 0);
        this.dataset.total = String(Math.max(0, total - discount));
        this.hidden = !cart.requires_shipping;
        this.render();
      });
      this.eventRoot = document.documentElement;
      this.eventRoot.addEventListener('cart:updated', this.onCartUpdated);
      if (this.querySelector('[data-shipping-reminder]')) {
        this.render();
      } else {
        this.initialRender = requestAnimationFrame(() => this.render());
      }
    }

    disconnectedCallback() {
      this.revision += 1;
      cancelAnimationFrame(this.initialRender);
      this.eventRoot?.removeEventListener('cart:updated', this.onCartUpdated);
    }

    getTiers() {
      return (this.dataset.rates || '').split(/[\n,]+/).flatMap((line) => {
        const parts = line.trim().split(':').map((part) => part.trim());
        if (![3, 4].includes(parts.length) || parts[0].toUpperCase() !== this.dataset.country) return [];
        if (!parts.slice(1).every((part) => /^\d+(?:\.\d{1,2})?$/.test(part))) return [];
        const minimum = Number(parts[1]);
        const fee = Number(parts[2]);
        const maximum = parts.length === 4 ? Number(parts[3]) : Infinity;
        return Number.isFinite(minimum) && Number.isFinite(fee) && maximum >= minimum
          ? [{ minimum, maximum, fee }] : [];
      }).sort((a, b) => a.minimum - b.minimum);
    }

    async render() {
      const revision = ++this.revision;
      const reminder = this.querySelector('[data-shipping-reminder]');
      const estimate = this.querySelector('[data-shipping-estimate]');
      if (!reminder) return;
      const currency = this.dataset.currency;
      const total = Number(this.dataset.total);
      const thresholdUsd = Number(this.dataset.thresholdUsd);

      try {
        const converter = window.G2GFreeShippingThreshold;
        const rate = currency === 'USD' ? 1 : await converter?.convertFromUsd(1, currency);
        if (!Number.isFinite(rate) || rate <= 0 || !Number.isFinite(total)) throw new Error('Unavailable estimate');
        if (!this.isConnected || revision !== this.revision) return;

        const money = new Intl.NumberFormat(document.documentElement.lang || undefined, {
          style: 'currency', currency,
          currencyDisplay: 'code'
        });
        const threshold = thresholdUsd > 0
          ? currency === 'USD' ? Math.round(thresholdUsd * 100) : Math.ceil(thresholdUsd * rate - 1e-9) * 100
          : 0;
        const unlocked = threshold > 0 && total >= threshold;
        const tiers = this.getTiers();
        const tier = tiers.filter((item) => total >= Math.round(item.minimum * rate * 100)
          && (item.maximum === Infinity || total <= Math.round(item.maximum * rate * 100))).at(-1);
        const hasBaseRate = tiers.some((item) => item.minimum === 0);
        const free = tier && hasBaseRate && (unlocked || tier.fee === 0);
        this.dataset.shippingEstimated = String(Boolean(tier && hasBaseRate));
        this.dataset.shippingUnlocked = String(unlocked || Boolean(free));
        this.dataset.shippingFree = String(Boolean(free));
        reminder.textContent = threshold > 0 && this.dataset.reminderEnabled === 'true'
          ? unlocked || free
            ? this.dataset.unlockedText
            : this.dataset.remainingTemplate.replace('[amount]', money.format((threshold - total) / 100))
          : this.dataset.defaultText;

        if (estimate) {
          estimate.textContent = tier && hasBaseRate
            ? free
              ? this.dataset.freeText
              : money.format(currency === 'USD'
                ? tier.fee : Math.ceil(tier.fee * rate - 1e-9))
            : this.dataset.fallbackText;
        }
      } catch {
        if (!this.isConnected || revision !== this.revision) return;
        this.dataset.shippingEstimated = 'false';
        this.dataset.shippingUnlocked = 'false';
        this.dataset.shippingFree = 'false';
        reminder.textContent = thresholdUsd > 0 && this.dataset.reminderEnabled === 'true'
          ? this.dataset.eligibleText : this.dataset.defaultText;
        if (estimate) estimate.textContent = this.dataset.fallbackText;
      }
    }
  }

  customElements.define('g2g-cart-shipping', G2GCartShipping);
})();
